"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import useCurrentUser from "../app/app/get-help/hooks/useCurrentUser";
import type { CurrentUser } from "../app/app/get-help/types";

type CurrentUserContextType = {
  /** Current user data from session API */
  user: CurrentUser | undefined;
  /** Whether user data is still loading */
  isLoading: boolean;
  /** Error from fetching user data */
  error: unknown;
};

const CurrentUserContext = createContext<CurrentUserContextType | null>(null);

type CurrentUserContextProviderProps = {
  children: ReactNode;
};

/**
 * Provider that fetches and provides current user data to the app.
 * This context should wrap components that need access to user data.
 */
export function CurrentUserContextProvider({
  children,
}: CurrentUserContextProviderProps) {
  const { user, isLoading, error } = useCurrentUser();

  const value = useMemo(
    () => ({
      user,
      isLoading,
      error,
    }),
    [user, isLoading, error],
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

/**
 * Hook to access current user data.
 * Must be used within a CurrentUserContextProvider.
 */
export function useCurrentUserContext(): CurrentUserContextType {
  const context = useContext(CurrentUserContext);
  if (!context) {
    throw new Error(
      "useCurrentUserContext must be used within a CurrentUserContextProvider",
    );
  }
  return context;
}

export default CurrentUserContext;
